import React, { useState } from 'react';
import { X } from 'lucide-react';
interface BrochureFormPopupProps {
  isOpen: boolean;
  onClose: () => void;
  brochureUrl: string;
}
export const BrochureFormPopup = ({
  isOpen,
  onClose,
  brochureUrl
}: BrochureFormPopupProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    budget: '',
    timeline: ''
  });
  if (!isOpen) return null;
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await fetch('https://hooks.zapier.com/hooks/catch/12304964/upn55f2/', {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          formType: 'brochure_download',
          brochureUrl
        })
      });
    } catch (err) {
      console.error('Webhook error:', err);
    }
    setIsSubmitting(false);
    window.open(brochureUrl, '_blank');
    onClose();
    setFormData({
      fullName: '',
      phone: '',
      email: '',
      budget: '',
      timeline: ''
    });
  };
  const selectStyle = {
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: 'right 0.5rem center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '1.5em 1.5em',
    paddingRight: '2.5rem'
  };
  const inputClasses = 'w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#d8a34d]/20 focus:border-[#d8a34d] outline-none transition-all text-gray-900 bg-gray-50/50 focus:bg-white';
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-64">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={onClose} data-id="element-65" />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-66">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 bg-white" data-id="element-67">
          <div className="pr-8" data-id="element-68">
            <h3 className="text-xl md:text-2xl font-display font-bold text-[#1a2e1c] leading-tight" data-id="element-69">
              Download Project Brochure
            </h3>
            <div className="w-12 h-1 bg-[#d8a34d] mt-3 rounded-full" data-id="element-70"></div>
            <p className="text-gray-500 text-sm mt-3" data-id="element-71">
              Get pricing, payment plans, floor plans & ROI details
            </p>
          </div>
          <button onClick={onClose} className="absolute top-5 right-5 text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100" aria-label="Close modal" data-id="element-72">
            <X className="w-5 h-5" data-id="element-73" />
          </button>
        </div>

        {/* Form */}
        <div className="overflow-y-auto p-6" data-id="element-74">
          <form id="brochure-form" onSubmit={handleSubmit} className="space-y-4" data-id="element-75">
            {/* Full Name */}
            <div className="space-y-1.5" data-id="element-76">
              <label htmlFor="brochure-fullName" className="block text-sm font-medium text-gray-700" data-id="element-77">
                Full Name *
              </label>
              <input type="text" id="brochure-fullName" name="fullName" required value={formData.fullName} onChange={handleChange} className={inputClasses} placeholder="John Doe" data-id="element-78" />
            </div>

            {/* Phone */}
            <div className="space-y-1.5" data-id="element-79">
              <label htmlFor="brochure-phone" className="block text-sm font-medium text-gray-700" data-id="element-80">
                Phone Number *
              </label>
              <input type="tel" id="brochure-phone" name="phone" required value={formData.phone} onChange={handleChange} className={inputClasses} placeholder="+1 (555) 000-0000" data-id="element-81" />
            </div>

            {/* Email */}
            <div className="space-y-1.5" data-id="element-82">
              <label htmlFor="brochure-email" className="block text-sm font-medium text-gray-700" data-id="element-83">
                Email Address *
              </label>
              <input type="email" id="brochure-email" name="email" required value={formData.email} onChange={handleChange} className={inputClasses} placeholder="john@example.com" data-id="element-84" />
            </div>

            {/* Investment Budget */}
            <div className="space-y-1.5" data-id="element-85">
              <label htmlFor="brochure-budget" className="block text-sm font-medium text-gray-700" data-id="element-86">
                Investment Budget
              </label>
              <select id="brochure-budget" name="budget" value={formData.budget} onChange={handleChange} className={`${inputClasses} appearance-none`} style={selectStyle} data-id="element-87">
                <option value="" data-id="element-88">Select a budget range</option>
                <option value="USD 545k to 600k" data-id="element-89">USD 545k to 600k</option>
                <option value="USD 600k to 1M" data-id="element-90">USD 600k to 1M</option>
                <option value="USD 1M+" data-id="element-91">USD 1M+</option>
              </select>
            </div>

            {/* Purchase Timeline */}
            <div className="space-y-1.5" data-id="element-92">
              <label htmlFor="brochure-timeline" className="block text-sm font-medium text-gray-700" data-id="element-93">
                Purchase Timeline
              </label>
              <select id="brochure-timeline" name="timeline" value={formData.timeline} onChange={handleChange} className={`${inputClasses} appearance-none`} style={selectStyle} data-id="element-94">
                <option value="" data-id="element-95">Select your timeline</option>
                <option value="Immediate" data-id="element-96">Immediate</option>
                <option value="3-6 Months" data-id="element-97">3-6 Months</option>
                <option value="Just Exploring" data-id="element-98">Just Exploring</option>
              </select>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50" data-id="element-99">
          <button type="submit" form="brochure-form" disabled={isSubmitting} className="w-full btn-gold px-6 py-3.5 rounded-lg font-bold text-base shadow-lg flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-100">
            {isSubmitting ? <>
                <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-101">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-102"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-103"></path>
                </svg>
                Processing...
              </> : 'Download Brochure'}
          </button>
        </div>
      </div>
    </div>;
};